import { collection, query, where, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { SearchResult } from '../models/SearchHistory';

const COLLECTION_NAME = 'searchHistory';

interface SearchHistoryEntry {
  id?: string;
  query: string;
  timestamp: Date;
  searchType: 'business';
  filters: {
    location: string[];
    industries: string[];
  };
  results: SearchResult[];
  resultsCount: number;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Check if a search combination already exists in Firestore
 * @param industry The industry to search for
 * @param location The formatted location string
 * @returns boolean indicating if the search is a duplicate
 */
export const checkDuplicateSearch = async (industry: string, location: string): Promise<boolean> => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('filters.industries', 'array-contains', industry),
      where('filters.location', 'array-contains', location)
    );
    
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking for duplicate search:', error);
    return false;
  }
};

/**
 * Add a new search to the search history
 * @param industry The industry searched for
 * @param location The formatted location string
 * @param results The search results
 * @returns The ID of the newly added search history entry
 */
export const addSearchToHistory = async (
  industry: string, 
  location: string,
  results: SearchResult[]
): Promise<string> => {
  try {
    const now = new Date();
    const searchHistory: Omit<SearchHistoryEntry, 'id'> = {
      query: `${industry} in ${location}`,
      timestamp: now,
      searchType: 'business',
      filters: {
        location: [location],
        industries: [industry]
      },
      results: results,
      resultsCount: results.length,
      createdAt: now,
      updatedAt: now
    };

    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...searchHistory,
      timestamp: Timestamp.fromDate(now),
      createdAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now)
    });

    return docRef.id;
  } catch (error) {
    console.error('Error adding search to history:', error);
    throw error;
  }
}; 